import React, { useState, useRef } from 'react';
import ModalDefault from '../../ModalDefault';
import ButtonDefault from '../../form/ButtonDefault';
import { IconClose, IconDownload, IconUpload } from '../../icons';
import { ModalWrapper, ModalTitle, ModalButtons } from '../../../pages/Demanda/styles';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { toastDefsError } from '../../../config/toastConfig';
import api from '../../../services/api';
import { X } from 'lucide-react';
import { comum } from '../../../styles/Themes/light';

const TextareaContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    label {
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.interfaceText400};
    }

    textarea {
        width: 100%;
        max-width: 100%;
        min-height: 120px;
        padding: 12px;
        border: 1px solid ${({ theme }) => theme.colors.interfaceBorder100};
        border-radius: 6px;
        font-size: 14px;
        font-family: inherit;
        resize: vertical;
        transition: border-color 0.2s;
        box-sizing: border-box;

        &:focus {
            outline: none;
            border-color: ${({ theme }) => theme.colors.primaryRed400};
        }

        &::placeholder {
            color: ${({ theme }) => theme.colors.interfaceText200};
        }
    }
`;

const UploadSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;

    .title {
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.interfaceText400};
    }

    .download-link {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        background: ${({ theme }) => theme.colors.interfacePure100};
        border: 1px solid ${({ theme }) => theme.colors.interfaceBorder100};
        border-radius: 6px;
        cursor: pointer;
        text-decoration: none;
        color: ${({ theme }) => theme.colors.interfaceText400};
        font-size: 14px;
        transition: all 0.2s;

        &:hover {
            background: ${({ theme }) => theme.colors.interfacePure200};
            border-color: ${({ theme }) => theme.colors.primaryRed400};
        }
    }

    .upload-area {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        border: 2px dashed ${({ theme }) => theme.colors.interfaceBorder200};
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s;
        background: ${({ theme }) => theme.colors.interfacePure50};

        &:hover {
            border-color: ${({ theme }) => theme.colors.primaryRed400};
            background: ${({ theme }) => theme.colors.interfacePure100};
        }

        input[type="file"] {
            position: absolute;
            width: 100%;
            height: 100%;
            opacity: 0;
            cursor: pointer;
        }

        .upload-content {
            display: flex;
            align-items: center;
            gap: 8px;
            color: ${({ theme }) => theme.colors.interfaceText400};
            font-size: 14px;
        }
    }

    .files-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 8px;

        .file-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 12px;
            background: ${({ theme }) => theme.colors.interfacePure100};
            border: 1px solid ${({ theme }) => theme.colors.interfaceBorder100};
            border-radius: 6px;
            font-size: 14px;
            color: ${({ theme }) => theme.colors.interfaceText400};

            .file-name {
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .file-remove {
                cursor: pointer;
                padding: 4px;
                border-radius: 4px;
                transition: all 0.2s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                
                &:hover {
                    background: ${({ theme }) => theme.colors.interfaceText100};
                }
            }
        }
    }

    .uploading {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        background: ${({ theme }) => theme.colors.interfacePure100};
        border: 1px solid ${({ theme }) => theme.colors.interfaceBorder100};
        border-radius: 6px;
        font-size: 14px;
        color: ${({ theme }) => theme.colors.interfaceText400};

        .spinner {
            width: 16px;
            height: 16px;
            border: 2px solid ${({ theme }) => theme.colors.interfaceBorder200};
            border-top-color: ${({ theme }) => theme.colors.primaryRed400};
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
            to { transform: rotate(360deg); }
        }
    }
`;

interface ArquivoConclusao {
    id_proposta_arquivo?: number;
    caminho?: string;
    nome: string;
    tipo?: 'arquivo_conclusao' | 'ata_processo';
    file?: File;
}

interface ModalConcluirDemandaProps {
    modalAtivo: boolean;
    setModalAtivo: (ativo: boolean) => void;
    onConfirmar: (observacoes: string, arquivosIds: number[]) => Promise<void>;
    loading?: boolean;
    idDemanda: number;
    isAudiencia?: boolean;
    onVoltar?: () => void;
}

export default function ModalConcluirDemanda({ 
    modalAtivo, 
    setModalAtivo, 
    onConfirmar,
    loading = false,
    idDemanda,
    isAudiencia = false,
    onVoltar
}: ModalConcluirDemandaProps) {
    const [observacoes, setObservacoes] = useState('');
    const [arquivosConclusao, setArquivosConclusao] = useState<ArquivoConclusao[]>([]);
    const [arquivosAta, setArquivosAta] = useState<ArquivoConclusao[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const inputConclusaoRef = useRef<HTMLInputElement>(null);
    const inputAtaRef = useRef<HTMLInputElement>(null);

    const handleSelecionarConclusao = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const MAX_FILE_BYTES = 50 * 1024 * 1024;
        if (file.size > MAX_FILE_BYTES) {
            toast.error(`Arquivo "${file.name}" excede 50MB.`, toastDefsError);
            e.target.value = '';
            return;
        }

        const novoArquivo: ArquivoConclusao = {
            nome: file.name,
            tipo: 'arquivo_conclusao',
            file: file
        };

        setArquivosConclusao(prev => [...prev, novoArquivo]);
        
        if (inputConclusaoRef.current) {
            inputConclusaoRef.current.value = '';
        }
    };

    const handleSelecionarAta = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const MAX_FILE_BYTES = 50 * 1024 * 1024;
        if (file.size > MAX_FILE_BYTES) {
            toast.error(`Arquivo "${file.name}" excede 50MB.`, toastDefsError);
            e.target.value = '';
            return;
        }

        const novoArquivo: ArquivoConclusao = {
            nome: file.name,
            tipo: 'ata_processo',
            file: file
        };

        setArquivosAta(prev => [...prev, novoArquivo]);
        
        if (inputAtaRef.current) {
            inputAtaRef.current.value = '';
        }
    };

    const handleRemoverConclusao = (index: number) => {
        setArquivosConclusao(prev => prev.filter((_, i) => i !== index));
    };

    const handleRemoverAta = (index: number) => {
        setArquivosAta(prev => prev.filter((_, i) => i !== index));
    };

    const handleConfirmar = async () => {
        setIsUploading(true);

        try {
            const todosArquivos = [...arquivosConclusao, ...arquivosAta];
            const arquivosIds: number[] = [];

            for (const arquivo of todosArquivos) {
                if (arquivo.file) {
                    try {
                        const formData = new FormData();
                        formData.append('arquivo', arquivo.file);
                        formData.append('tipo_acao', 'CONCLUSAO');
                        formData.append('tipo_arquivo', arquivo.tipo || 'arquivo_conclusao');

                        const response = await api.post(`proposta/${idDemanda}/upload-conclusao`, formData);

                        if (response.data.status !== 'success') {
                            throw new Error(response.data.message || 'Erro ao enviar arquivo');
                        }

                        if (response.data.result?.id_proposta_arquivo) {
                            arquivosIds.push(response.data.result.id_proposta_arquivo);
                        }
                    } catch (error: any) {
                        console.error('Erro ao enviar arquivo:', error);
                        const mensagemErro = error.response?.data?.message || error.response?.data?.error || error.message || 'Erro desconhecido';
                        toast.error(`Falha ao enviar "${arquivo.nome}": ${mensagemErro}`, toastDefsError);
                        setIsUploading(false);
                        return;
                    }
                } else if (arquivo.id_proposta_arquivo) {
                    arquivosIds.push(arquivo.id_proposta_arquivo);
                }
            }

            await onConfirmar(observacoes.trim(), arquivosIds);
            setIsUploading(false);
        } catch (error: any) {
            console.error('Erro ao processar arquivos:', error);
            toast.error(error.message || 'Erro ao processar arquivos', toastDefsError);
            setIsUploading(false);
        }
    };

    const handleCancelar = () => {
        setObservacoes('');
        setArquivosConclusao([]);
        setArquivosAta([]);
        setModalAtivo(false);
    };

    const handleVoltar = () => {
        setObservacoes('');
        setArquivosConclusao([]);
        setArquivosAta([]);
        setModalAtivo(false);
        if (onVoltar) {
            onVoltar();
        }
    };

    return (
        <ModalDefault
            id={'overlayModal'}
            onClose={handleCancelar}
            openModal={modalAtivo}
            width="600px"
            maxWidth="90vw"
        >
            <ModalWrapper>
                <ModalTitle>
                    <div className="points">
                        Concluir demanda
                    </div>
                    <div
                        className="close"
                        onClick={handleCancelar}
                    >
                        <IconClose />
                    </div>
                </ModalTitle>

                <div className="accept">
                    <UploadSection>
                        <div className="title">Caso necessite, faça download do arquivo de conclusão abaixo:</div>
                        <a 
                            href="/formulario-conclusao.docx" 
                            target="_blank" 
                            rel="noreferrer"
                            className="download-link"
                        >
                            <IconDownload />
                            Arquivo de conclusão
                        </a>

                        <div className="title">Arquivo de conclusão preenchido:</div>
                        <div className="upload-area">
                            <input
                                ref={inputConclusaoRef}
                                type="file"
                                onChange={handleSelecionarConclusao}
                                disabled={isUploading}
                            />
                            <div className="upload-content">
                                <IconUpload color={comum.colors.interfaceText400} />
                                <span>Clique para fazer upload</span>
                            </div>
                        </div>


                        {arquivosConclusao.length > 0 && (
                            <div className="files-list">
                                {arquivosConclusao.map((arquivo, index) => (
                                    <div key={index} className="file-item">
                                        <span className="file-name">{arquivo.nome}</span>
                                        <div
                                            className="file-remove"
                                            onClick={() => handleRemoverConclusao(index)}
                                            title="Remover arquivo"
                                        >
                                            <X size={16} color={comum.colors.interfaceDanger400} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {isAudiencia && (
                            <>
                                <div className="title">Ata do Processo:</div>
                                <div className="upload-area">
                                    <input
                                        ref={inputAtaRef}
                                        type="file"
                                        onChange={handleSelecionarAta}
                                        disabled={isUploading}
                                    />
                                    <div className="upload-content">
                                        <IconUpload color={comum.colors.interfaceText400} />
                                        <span>Clique para fazer upload</span>
                                    </div>
                                </div>

                                {arquivosAta.length > 0 && (
                                    <div className="files-list">
                                        {arquivosAta.map((arquivo, index) => (
                                            <div key={index} className="file-item">
                                                <span className="file-name">{arquivo.nome}</span>
                                                <div
                                                    className="file-remove"
                                                    onClick={() => handleRemoverAta(index)}
                                                    title="Remover arquivo"
                                                >
                                                    <X size={16} color={comum.colors.interfaceDanger400} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </UploadSection>

                    <TextareaContainer>
                        <label>Observações (opcional)</label>
                        <textarea
                            placeholder="Escreva aqui sua mensagem de conclusão"
                            value={observacoes}
                            onChange={(e) => setObservacoes(e.target.value)}
                            disabled={loading || isUploading}
                        />
                    </TextareaContainer>
                </div>

                <ModalButtons>
                    <ButtonDefault
                        className="cancel"
                        onClick={handleVoltar}
                        disabled={loading || isUploading}
                    >
                        Voltar
                    </ButtonDefault>
                    <ButtonDefault
                        className="continue"
                        onClick={handleConfirmar}
                        disabled={loading || isUploading}
                    >
                        {loading || isUploading ? 'Processando...' : 'Confirmar'}
                    </ButtonDefault>
                </ModalButtons>
            </ModalWrapper>
        </ModalDefault>
    );
}

